import { call, put, select } from 'redux-saga/effects';

import { extractErrorMessage, sendToLogger } from 'connect/saga/helpers';

import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { POST, request } from 'utils/request';

import { SagaHandler } from '../../SagaHandler';

const apiCheckOtp = ({ email }) => request(
  POST,
  `${CONFIG.API_URL}/auth/patient-otp/check`,
  {
    email,
  },
);

const apiRequestOtp = ({ emailOrPhone }) => request(
  POST,
  `${CONFIG.API_URL}/auth/patient-otp/request`,
  {
    emailOrPhone,
  },
);

const isStatusSent = ({ status, sent } = {}) => status === 'sent' || Boolean(sent?.email) || Boolean(sent?.phone);

/*
  Checks if an OTP has been sent to the user
  If not, logs an error and requests a new OTP to be sent
*/
class _CheckOtp extends SagaHandler {
  constructor() {
    super({
      actionName: 'data/CHECK_OTP',
      stateKey: 'checkOtp',
    });
  }

  createAction(userData, match, matchToken) {
    return {
      type: this._actionName,
      userData,
      match,
      matchToken,
    };
  }

  * saga() {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    const valueHandler = this.value;

    try {
      const checkoutState = yield select(CheckoutStateHandler.selector);
      const { email } = checkoutState?.account || {};

      if (!email) {
        throw new Error('No email in Redux state, cannot check if OTP was sent');
      }

      yield put(loadingHandler.update(true));
      yield put(errorHandler.update(null));
      yield put(valueHandler.update(null));

      const otpStatus = yield call(apiCheckOtp, { email });
      yield put(valueHandler.update(otpStatus));

      // OTP was sent, we're good
      if (isStatusSent(otpStatus)) {
        return;
      }

      // OTP not sent - expired, too many attempts, or not created
      const resentOtpStatus = yield call(apiRequestOtp, { emailOrPhone: email });
      yield put(valueHandler.update(resentOtpStatus));

      // OTP was resent, we're good
      if (isStatusSent(resentOtpStatus)) {
        return;
      }

      // Failed on re-send, something is wrong
      throw new Error('Failed to send OTP', { resentOtpStatus });
    } catch (error) {
      const errorFromApi = error.response?.data;
      const errorMessage = extractErrorMessage(error);

      yield put(errorHandler.update(errorFromApi || errorMessage));
      yield put(valueHandler.update(null));

      sendToLogger(error, 'CheckOtp');
    } finally {
      yield put(loadingHandler.update(false));
    }
  }
}

export const CheckOtp = new _CheckOtp();
